import JSZip from 'jszip'

/**
 * The parts of a Word document a save rewrote, keyed by their path inside it.
 *
 * A .docx is a zip of XML parts and media, and leaving a comment on one rewrites
 * a handful of the XML while the images — nearly all of a real proposal's bytes —
 * stay exactly as they were. A null is a part that is gone: the last comment
 * leaving a document takes its part with it rather than emptying it.
 */
export type DocumentParts = Record<string, string | null>

/** The part holding the document's own text, without which there is no document. */
export const DOCUMENT_PART_PATH = 'word/document.xml'

export const NOT_A_DOCUMENT_PART_SET_MESSAGE =
  'The document could not be prepared for saving.'

/**
 * Whether these parts describe a document, rather than what a failed export
 * leaves behind.
 *
 * There is no undo on the other side of a save, and an exporter that fails
 * quietly hands back nothing at all — which would otherwise be merged into a
 * proposal somebody spent a week on.
 */
export const isDocumentPartSet = (parts: DocumentParts): boolean =>
  typeof parts[DOCUMENT_PART_PATH] === 'string' &&
  parts[DOCUMENT_PART_PATH].length > 0

/**
 * The changed parts alone, zipped, as a save uploads them.
 *
 * Nothing stands for the folders the paths run through. A .docx names its parts
 * by full path and never looks for a folder, and leaving them out keeps this
 * archive and the one the server merges from holding exactly the same entries.
 */
export const packDocumentParts = async (
  parts: DocumentParts,
): Promise<Blob> => {
  const archive = new JSZip()
  for (const [path, xml] of Object.entries(parts)) {
    if (xml !== null) {
      archive.file(path, xml, { createFolders: false })
    }
  }
  return await archive.generateAsync({ type: 'blob', compression: 'DEFLATE' })
}

/**
 * The whole document, with these parts merged into the bytes it was opened from.
 *
 * The same merge the server performs on the stored file, run here so that the
 * copy this browser holds goes on being the document too. Everything the save
 * did not touch is carried over as it was stored, which is why this costs a
 * fraction of building the document from scratch.
 */
export const mergeDocumentParts = async (
  original: Blob,
  parts: DocumentParts,
): Promise<Blob> => {
  const archive = await JSZip.loadAsync(await original.arrayBuffer())
  for (const [path, xml] of Object.entries(parts)) {
    if (xml === null) {
      archive.remove(path)
    } else {
      archive.file(path, xml, { createFolders: false })
    }
  }
  return await archive.generateAsync({ type: 'blob', compression: 'DEFLATE' })
}

/** The paths this save removed, which an archive of parts cannot itself carry. */
export const deletedDocumentParts = (parts: DocumentParts): string[] =>
  Object.entries(parts)
    .filter(([, xml]) => xml === null)
    .map(([path]) => path)
